import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { useCanManageUsers } from '../auth/useIsAdmin';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { TabBar } from './TabBar';
import { NAV_ITEMS } from './navigation';
import type { ModuleTone } from '../ui/ModuleSection';
import { useAnnouncementNotifications } from '../notifications/useAnnouncementNotifications';

/** Literal class strings so Tailwind sees them. One per module, same pastel as its tab. */
const WASH: Record<ModuleTone, string> = {
  announcements: 'from-module-announcements',
  complaints: 'from-module-complaints',
  events: 'from-module-events',
  carpooling: 'from-module-carpooling',
  restaurant: 'from-module-restaurant',
  library: 'from-module-library',
};

/**
 * The signed-in frame: a compact header, the navigation, the screen.
 *
 * **The navigation lives in `TabBar`, and the table it reads lives in
 * `navigation.ts`.** Until the UI audit of 2026-09-21 this file held the table
 * and rendered it three times - a sidebar from `md`, a drawer below it, and the
 * drawer's own copy of the management block - which is how the product ended up
 * with a hamburger on the one surface DESIGN.md forbids it on (§Layout, The No
 * Hamburger Rule). Three copies of eight items also meant the Restaurant entry
 * could be, and was, correct in one and stale in another.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { signOut, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const canManage = useCanManageUsers();

  // Active browser notifications listener for announcements (respects opt-in)
  useAnnouncementNotifications();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // A route change closes the menu: a panel that survives the navigation it
  // triggered hangs over the new screen until the next click anywhere.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const module = NAV_ITEMS.find((item) => location.pathname.startsWith(item.to))?.module;
  const wash = module && WASH[module];

  const initials =
    user && user.firstName && user.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
      : 'U';

  return (
    <div className="min-h-screen flex bg-surface">
      {/* DESIGN.md §Layout: 240px, and only from `lg`. Below that the six
          destinations are on the bar at the bottom of the screen. */}
      {/* DESIGN.md §4 Navigation: transparent, no border. The rail sits on the
          canvas itself; only the active pill and the wordmark mark it out. */}
      <aside className="hidden lg:flex flex-col w-60 bg-surface sticky top-0 h-screen">
        <div className="px-6 pt-7 pb-8">
          <div className="flex items-baseline gap-1.5 font-display text-[1.75rem] leading-none tracking-[-0.03em] text-on-surface">
            MySCAW
          </div>
          <div className="mt-1.5 font-label-sm text-label-sm text-on-surface-variant">
            ISCAE Casablanca
          </div>
        </div>

        <TabBar variant="rail" canManage={canManage} />
      </aside>

      <main className="relative flex-1 min-w-0">
        {/*
          The colour of the place you are in. The source site carries each
          section on a pastel panel; here every module opens on a wash of its
          own pastel that fades into the canvas, so the tab bar's colour and the
          screen's colour are the same fact. Decorative, and it scrolls away.
        */}
        {wash && (
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b ${wash} to-surface lg:rounded-tl-3xl`}
          />
        )}
        {/*
          One header at every width. It was two - a desktop bar and a mobile bar
          with a hamburger in it - which is two places to change a header and one
          place to forget. Glass, so the wash shows through it.
        */}
        <div className="sticky top-0 z-20 backdrop-blur-md px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between pt-[calc(env(safe-area-inset-top,0px)+0.75rem)]">
          {/* The wordmark is on the rail from `lg`; repeating it here would be
              the same name twice in one viewport. */}
          <div className="flex items-baseline gap-1.5 min-w-0 font-display text-[1.5rem] leading-none tracking-[-0.03em] text-on-surface lg:invisible">
            MySCAW
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 min-h-[44px] pl-3 pr-1 rounded-full hover:bg-primary/[0.06] transition-colors duration-chip"
                aria-label={t('common.profileMenu')}
                aria-expanded={menuOpen}
              >
                <span className="text-right leading-tight hidden sm:block font-title-md text-title-md text-on-surface">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="w-9 h-9 rounded-full bg-primary-container overflow-hidden flex items-center justify-center flex-shrink-0">
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-on-primary-container font-title-md text-[13px]">
                      {initials}
                    </span>
                  )}
                </span>
              </button>

              {menuOpen && (
                <div className="absolute right-0 z-30 mt-2 min-w-[240px] max-w-[calc(100vw-1rem)] bg-surface-container-lowest rounded-2xl shadow-soft border border-hairline py-2 overflow-hidden">
                  <div className="px-4 py-3 border-b border-outline-variant/40 mb-1">
                    <div className="font-title-md text-title-md text-on-surface">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="font-label-sm text-label-sm text-on-surface-variant truncate">
                      {user?.email}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/dashboard/profile')}
                    className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px] text-on-surface-variant font-label-md hover:bg-surface-container transition-colors duration-chip"
                  >
                    <span className="material-symbols-outlined text-[20px]">person</span>
                    {t('nav.profile')}
                  </button>
                  {/*
                    Management reaches a phone here rather than on the bar. The
                    bar is six module destinations and stays six; an admin screen
                    is not a seventh module (navigation.ts says why).
                  */}
                  {canManage && (
                    <button
                      onClick={() => navigate('/dashboard/admin/users')}
                      className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px] text-on-surface-variant font-label-md hover:bg-surface-container transition-colors duration-chip"
                    >
                      <span className="material-symbols-outlined text-[20px]">group</span>
                      {t('nav.users')}
                    </button>
                  )}
                  <div className="my-1 border-t border-outline-variant/50" />
                  <button
                    onClick={() => {
                      signOut();
                      navigate('/');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px] text-error font-label-md hover:bg-error-container/40 transition-colors duration-chip"
                  >
                    <span className="material-symbols-outlined text-[20px]">logout</span>
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/*
          The bottom padding is the bar's height plus the safe-area inset: the
          last card of a list has to clear it, or the final row of every screen
          in the product is unreadable and untappable on a phone.
        */}
        <div
          key={location.pathname}
          className="route-enter relative px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto pb-[calc(56px+env(safe-area-inset-bottom,0px)+1.5rem)] lg:pb-8"
        >
          {children}
        </div>
      </main>

      <TabBar variant="bar" canManage={canManage} />
    </div>
  );
}
